var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var Autocomplete_1;
import React, { Component, createRef } from 'react';
import debounce from 'lodash.debounce';
import InfiniteScroll from 'react-infinite-scroller';
import { Alignment, AutocompleteType, Breakpoint, FirstPage, ModalType, SensitiveData } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType, Container } from '@philips-internal/oswald-ui-core';
import { EditorContext } from '@philips-internal/oswald-ui-core/lib/component/context/index.js';
import { arrayOf, bool, element, eventListener, expression, formatters, number, object, oneOf, oneOfType, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { ChangeEvent, FocusEvent, PageEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { MessageService, ModelService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { classNames, defaultClasses, enumValues, fuzzySearch, getBreakpoint, isDefined, isEqualShallow, isFunction, isObject, isTouchableDevice, shallowCompare } from '@philips-internal/oswald-ui-utils';
import { isPrimaryInputTouch } from '../../utils/device-capabilities.js';
import { Input } from '../input/input.js';
import { Modal } from '../modal/modal.js';
import { Popover } from '../popover/popover.js';
import { SelectableIterator } from '../selectable-iterator/selectable-iterator.js';
import { AutocompleteAPI } from './autocomplete-api.js';
import { FakeInput, ModalInput } from './components.js';
import './autocomplete.css';
const AutocompleteContainer = {
    acceptedTypes: [
        ComponentType.BEHAVIOR,
        ComponentType.LIST,
    ],
    name: '',
};
let Autocomplete = Autocomplete_1 = class Autocomplete extends Component {
    constructor() {
        super(...arguments);
        this.inputRef = createRef();
        this.modalRef = createRef();
        this.state = {
            currentPage: 0,
            focused: false,
            internalValue: [],
            open: false,
            query: '',
        };
        this.onClearClick = () => {
            this.setState({ internalValue: [], query: '' }, this.triggerOnChange);
        };
        this.onClose = () => {
            this.setState({ open: false });
        };
        this.onInputBlur = () => {
            const { onBlur } = this.props;
            if (isFunction(onBlur)) {
                onBlur(new FocusEvent());
            }
            this.setState({ focused: false });
        };
        this.onInputChange = (event) => {
            const { value } = event;
            const { firstPage, minSearchLength } = this.props;
            const meetsSearchLength = value.length > 0 && (Number(minSearchLength) <= 0 || value.length >= Number(minSearchLength));
            this.setState({ currentPage: Number(firstPage), open: isPrimaryInputTouch() || meetsSearchLength, query: value }, () => {
                if (!meetsSearchLength) {
                    return;
                }
                this.$search();
            });
        };
        this.onInputFocus = (search = true) => {
            const { readOnly, onFocus } = this.props;
            if (readOnly) {
                isFunction(onFocus) && onFocus(new FocusEvent());
                return;
            }
            if (!this.shouldOpenOnFocus()) {
                return this.setState({ focused: true });
            }
            this.inputRef.current?.selectInput();
            this.setState({ open: true }, () => {
                if (!this.shouldSkipSearch() && search) {
                    this.$search();
                }
                if (isFunction(onFocus)) {
                    onFocus(new FocusEvent());
                }
            });
        };
        this.onItemSelected = (event) => {
            const { multiselect } = this.props;
            const { value } = event;
            this.setState({
                internalValue: value,
                open: multiselect,
            }, this.triggerOnChange);
        };
        this.onLoadMore = () => {
            const { done, loading, onNextPage } = this.props;
            if (done || loading || !isFunction(onNextPage)) {
                return;
            }
            this.setState(({ currentPage }) => ({ currentPage: currentPage + 1 }), () => {
                onNextPage(this.getPageEvent(this.state.currentPage, true));
            });
        };
        this.onModalInputFocus = () => {
            if (this.shouldSkipSearch()) {
                return;
            }
            this.$search();
        };
        this.$search = debounce(this.search, 300);
        this.renderExternalAction = () => {
            const { clearable, enabled = true, readOnly } = this.props;
            const { internalValue, open } = this.state;
            if (!enabled || readOnly) {
                return null;
            }
            if (open || internalValue.length === 0 || !clearable) {
                const openOptions = () => {
                    this.setState({ open: true }, this.$search);
                };
                return (React.createElement("button", { "aria-label": MessageService.translate('tws.autocomplete.show-options'), className: "c-autocomplete__action-button", onClick: openOptions, type: "button" },
                    React.createElement("span", { className: "i-collapse-arrow" })));
            }
            return (React.createElement("button", { "aria-label": MessageService.translate('tws.autocomplete.clear'), className: "c-autocomplete__action-button", onClick: this.onClearClick, type: "button" },
                React.createElement("span", { className: "i-clear" })));
        };
        this.getInputValue = () => {
            const { focused, open, query } = this.state;
            if (open || focused) {
                return query;
            }
            return this.getDescriptionValue();
        };
    }
    /**
     * Prettifies a given the entry of value according to formatters.
     *
     * @param properties - Properties to generate pretty value.
     */
    static formatPrettyValue(properties) {
        const { entry, formatters, multiselect, previewAmount, value } = properties;
        if (!value) {
            return;
        }
        const valueIsArray = Array.isArray(value);
        const slicedValue = valueIsArray
            ? value.slice(0, previewAmount)
            : [];
        const item = !valueIsArray
            ? value[entry]
            : slicedValue.map(v => v[entry]).join(', ');
        if (!item) {
            return;
        }
        let message = MessageService.translate(String(item));
        if (formatters && 'entry' in formatters) {
            message = MessageService
                .translate(ModelService.format(formatters.entry.name, item, formatters.entry.options));
        }
        const moreValuesMessage = multiselect && valueIsArray && value.length > previewAmount
            ? `+ ${MessageService.translate('tws.autocomplete.more', { quantity: value.length - slicedValue.length })}`
            : '';
        return `${message} ${moreValuesMessage}`.trim();
    }
    componentDidMount() {
        const { firstPage, value } = this.props;
        if (value) {
            this.applyValueToState();
        }
        this.setState({ currentPage: Number(firstPage) });
    }
    componentDidUpdate(prevProps) {
        const { firstPage, reset, value } = this.props;
        const { reset: oldReset, value: oldValue } = prevProps;
        if (Boolean(reset) && reset !== oldReset) {
            this.setState({ currentPage: Number(firstPage) });
        }
        if (value === oldValue || (isEqualShallow(value, oldValue))) {
            return;
        }
        if (!value) {
            this.setState({ internalValue: [] });
        }
        this.applyValueToState();
    }
    shouldComponentUpdate(nextProps, nextState) {
        const stateChanged = shallowCompare(this.state, nextState, [
            'focused',
            'internalValue',
            'localSearchData',
            'open',
            'query',
        ]);
        if (stateChanged) {
            return true;
        }
        return shallowCompare(this.props, nextProps, [
            'children',
            'className',
            'data',
            'done',
            'enabled',
            'entry',
            'formatters',
            'limit',
            'loading',
            'localData',
            'minSearchLength',
            'multiselect',
            'placeholder',
            'primaryKey',
            'queryKey',
            'readOnly',
            'reset',
            'sensitiveData',
            'strikethrough',
            'title',
            'uuid',
            'valid',
            'value',
        ]);
    }
    applyValueToState() {
        const { value } = this.props;
        if (!value) {
            return;
        }
        if (value && isDefined(value)) {
            if (Array.isArray(value) && value.every((value) => isObject(value))) {
                return this.setState({ internalValue: value });
            }
            if (!Array.isArray(value) && isObject(value)) {
                return this.setState({ internalValue: [value] });
            }
        }
        throw new Error('[Autocomplete]: Trying to use a non-object value for the value property.');
    }
    shouldSkipSearch() {
        return this.state.currentPage > Number(this.props.firstPage);
    }
    search() {
        const { data, firstPage, localData, onNextPage, queryKey } = this.props;
        const { query } = this.state;
        if (localData) {
            if (query.length === 0) {
                return this.setState({ localSearchData: data });
            }
            const searchOptions = {
                threshold: 0.2,
            };
            const localSearchData = query
                ? fuzzySearch(data, [queryKey], query, searchOptions).map((result) => result.item)
                : undefined;
            return this.setState({ localSearchData });
        }
        this.setState({ currentPage: Number(firstPage), open: true }, () => {
            if (isFunction(onNextPage)) {
                onNextPage(this.getPageEvent(Number(firstPage), false));
            }
        });
    }
    triggerOnChange() {
        const { multiselect, onChange, value: oldValue } = this.props;
        const { internalValue } = this.state;
        const value = internalValue.length === 0
            ? undefined
            : multiselect
                ? internalValue
                : internalValue[0];
        if (isFunction(onChange)) {
            onChange(new ChangeEvent({
                oldValue,
                prettyValue: this.getDescriptionValue(),
                value,
            }));
        }
    }
    shouldOpenOnFocus() {
        const { enabled, minSearchLength, readOnly } = this.props;
        const { query } = this.state;
        const isEnabled = (enabled || !readOnly);
        const meetsSearchLength = Number(minSearchLength) <= 0 || query.length >= Number(minSearchLength);
        return (isEnabled && (meetsSearchLength || isTouchableDevice())) || Boolean(this.context?.isDesigning());
    }
    getClassName() {
        const { readOnly, enabled, className, clearable, strikethrough } = this.props;
        const { internalValue, open, query } = this.state;
        return classNames({
            'c-autocomplete': true,
            'has-value': internalValue.length > 0 || query.length > 0,
            'is-clearable': Boolean(clearable),
            'is-open': open,
            'u-text-strikethrough': Boolean(strikethrough),
            [`${className}`]: Boolean(className),
            ...defaultClasses({
                enabled: Boolean(enabled),
                readOnly: Boolean(readOnly),
            }),
        });
    }
    getDescriptionValue() {
        const { entry, formatters, multiselect, previewAmount } = this.props;
        const { internalValue } = this.state;
        return Autocomplete_1.formatPrettyValue({ entry, formatters, multiselect, previewAmount, value: internalValue }) || '';
    }
    getLimit() {
        const { limit } = this.props;
        const breakpoint = getBreakpoint();
        if (breakpoint === Breakpoint.XSM) {
            return Math.floor(limit * 1.4);
        }
        if (breakpoint === Breakpoint.SM) {
            return Math.floor(limit * 2);
        }
        return limit;
    }
    getPageEvent(page, accumulate) {
        const { queryKey } = this.props;
        const { query } = this.state;
        return new PageEvent({
            accumulate,
            limit: this.getLimit(),
            page,
            params: {
                [queryKey]: query,
            },
        });
    }
    renderContent() {
        const { children, data, done, emptyResultExpression, firstPage, loading, localData, multiselect, primaryKey, uuid } = this.props;
        const { currentPage, internalValue, localSearchData } = this.state;
        const hasMore = localData ? false : !done;
        const scrollParent = this.modalRef.current
            ? this.modalRef.current.getModalScrollContent()
            : null;
        const selectableIteratorData = localData && localSearchData
            ? localSearchData
            : data;
        const hideLoadedData = loading && currentPage <= Number(firstPage);
        return (React.createElement(InfiniteScroll, { className: "c-autocomplete__content", getScrollParent: () => scrollParent, hasMore: hasMore, initialLoad: localData, loadMore: this.onLoadMore, pageStart: Number(firstPage), threshold: 100, useWindow: false },
            React.createElement(SelectableIterator, { data: hideLoadedData ? [] : selectableIteratorData, done: (localData || Boolean(done)) && !loading, emptyResultExpression: emptyResultExpression, loading: Boolean(loading), multiselect: multiselect, onSelect: this.onItemSelected, primaryKey: primaryKey, selected: internalValue, uuid: uuid ?? '' }, children)));
    }
    renderInput() {
        const { enabled, placeholder, readOnly, sensitiveData, valid } = this.props;
        return (React.createElement("div", { className: "c-autocomplete__search" },
            React.createElement(Input, { autoComplete: AutocompleteType.OFF, autoCorrect: false, enabled: enabled, externalAction: this.renderExternalAction(), onBlur: this.onInputBlur, onChange: this.onInputChange, onFocus: () => this.onInputFocus(), placeholder: placeholder, readOnly: readOnly, ref: this.inputRef, sensitiveData: sensitiveData, valid: valid, value: this.getInputValue() })));
    }
    renderMobile() {
        const { enabled, placeholder, readOnly, title, valid } = this.props;
        const { open, query } = this.state;
        return (React.createElement("div", { className: this.getClassName() },
            React.createElement(FakeInput, { enabled: enabled, externalAction: this.renderExternalAction(), onFocus: () => this.onInputFocus(false), placeholder: placeholder, readOnly: readOnly, valid: valid, value: this.getDescriptionValue() }),
            open && !readOnly && (React.createElement(Modal, { className: "c-autocomplete__modal", focusOnOpen: false, noPadding: true, onClose: this.onClose, ref: this.modalRef, title: title, type: ModalType.FULLSCREEN },
                React.createElement(ModalInput, { onBlur: this.onInputBlur, onChange: this.onInputChange, onFocus: this.onModalInputFocus, placeholder: placeholder, value: query }),
                this.renderContent()))));
    }
    render() {
        if (isPrimaryInputTouch()) {
            return this.renderMobile();
        }
        const { readOnly, uuid } = this.props;
        const { open } = this.state;
        return (React.createElement(Container, { acceptedTypes: AutocompleteContainer.acceptedTypes, className: this.getClassName(), uuid: uuid },
            React.createElement(Popover, { alignment: Alignment.LEFT, matchTargetWidth: true, maxHeight: 320, onClose: this.onClose, open: open && !readOnly, padding: 0, showArrow: false },
                this.renderInput(),
                React.createElement(Popover.Content, null, this.renderContent()))));
    }
};
Autocomplete.contextType = EditorContext;
Autocomplete.propTypes = {
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    className: string,
    clearable: bool,
    data: arrayOf(object).notEnumerable(),
    done: bool.notEnumerable(),
    emptyResultExpression: expression,
    enabled: bool,
    entry: string,
    firstPage: oneOf(enumValues(FirstPage)),
    formatters: formatters,
    limit: number,
    loading: bool.notEnumerable(),
    localData: bool,
    minSearchLength: number,
    multiselect: bool,
    onBlur: eventListener,
    onChange: eventListener,
    onFocus: eventListener,
    onNextPage: eventListener,
    placeholder: expression,
    previewAmount: number,
    primaryKey: string,
    queryKey: string,
    readOnly: bool,
    reset: bool.notEnumerable(),
    sensitiveData: oneOf(enumValues(SensitiveData)),
    strikethrough: bool,
    title: expression,
    uuid: string,
    valid: bool.notEnumerable(),
    value: oneOfType([object, arrayOf(object)]).notEnumerable(),
};
Autocomplete.defaultProps = {
    clearable: true,
    enabled: true,
    entry: '',
    firstPage: FirstPage.ZERO,
    limit: 12,
    localData: false,
    minSearchLength: 3,
    multiselect: false,
    previewAmount: 1,
    primaryKey: 'id',
    queryKey: 'query',
    sensitiveData: SensitiveData.NONE,
    uuid: '',
    valid: true,
};
Autocomplete = Autocomplete_1 = __decorate([
    AwesomeComponent({ api: AutocompleteAPI, containers: [AutocompleteContainer], type: ComponentType.DATA_ENTRY })
], Autocomplete);
export { Autocomplete, };
//# sourceMappingURL=autocomplete.js.map